import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { CreationContactContext } from './CreationContactContext';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import GroupApi from '../../../core/apis/GroupApi';
import { isEmpty } from 'lodash';
import UserAuthorizationDatagrid from '../userauthorization/UserAuthorizationDatagrid';
import { useGetAuthorizations } from '../../investors/copyContactsSteps/ContactsStep';
import {
  useFetchAuthorizationsForDatagrid,
  createUserAuthorizations
} from '../userauthorization/stepper/utils';

const useStyles = makeStyles({
  label: {
    color: '#424242',
    fontSize: '14px',
    display: 'inline-block'
  },
  topToolbar: {
    paddingTop: 0,
    paddingBottom: 0
  },
  radioLabel: {
    fontSize: '14px'
  },
  radioLabelRoot: {
    marginRight: '0px !important'
  },
  contactInformation: {
    fontSize: '14px'
  },
  bold: {
    fontWeight: '600'
  }
});

const AssignPermissionGroupsStep = props => {
  const {
    creation,
    contactCreated,
    permissions,
    connectedUserProfile,
    selectedAssignmentMode,
    selectedContactForImport,
    permissionGroupsUploaded,
    setPermissionsStaging,
    permissionsStaging,
    authorizationsGridToDeleteRef,
    isNewImportToDo
  } = props;
  const classes = useStyles();
  const notifyServerError = useNotifyServerError();
  const [groupsFilter, setGroupsFilter] = useState('ALL');
  const {
    userType: createdUserType,
    userId: createdContactId
  } = contactCreated;
  const [groups, setGroups] = useState([]);
  const { contactContext } = useContext(CreationContactContext);
  const isCreatedContactRcOrIa =
    contactCreated.userType === 'REGULAR_CONTACT' ||
    contactCreated.userType === 'INVESTOR_ADMIN';
  const { data: connectedUserAuthorizations } = useGetAuthorizations(
    connectedUserProfile
  );
  const [currentAuthorizationsIds, setCurrentAuthorizationsIds] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);

  /**
   To prevent unnecessary API calls when using the same custom hook (useFetchAuthorizationsForDatagrid)
   for both imported and created contacts:

   -We utilize two boolean props (arePermissionGroupsUploaded and isNewImportToDo) that are passed to these hooks.

   -This approach ensures that we avoid making redundant calls when they are not required.

   -For instance, in the case of the imported contact, we pass true to "arePermissionGroupsUploaded" even when it's not
   actually uploaded, as this prop is necessary for the created contact.Similarly, for the created contact,
   we pass true to "isNewImportToDo" even when it's not relevant, as this prop is only useful for the imported contact call.
   */

  const {
    authorizations: importedAuthorizations,
    authorizationsIds: importedAuthorizationsIds
  } = useFetchAuthorizationsForDatagrid(
    selectedContactForImport?.userId,
    createdContactId,
    selectedAssignmentMode,
    isCreatedContactRcOrIa,
    true,
    isNewImportToDo
  );

  const {
    authorizations: createdContactAuthorizations,
    authorizationsIds: createdContactAuthorizationsIds
  } = useFetchAuthorizationsForDatagrid(
    createdContactId,
    createdContactId,
    selectedAssignmentMode,
    isCreatedContactRcOrIa,
    !isEmpty(permissionGroupsUploaded),
    true
  );

  // Determinate ONLY-ASSIGN or ALL (show authorizations)
  useEffect(() => {
    if (
      selectedAssignmentMode === 'import' ||
      permissionGroupsUploaded.length > 0
    ) {
      setGroupsFilter('ONLY_ASSIGNED');
    }
  }, [selectedAssignmentMode, permissionGroupsUploaded.length]);

  // Fetch Groups and setGroups()
  useEffect(() => {
    GroupApi.getActiveNonAdminGroups()
      .then(({ pageData: groupsFetched }) => {
        setGroups(groupsFetched);
      })
      .catch(e => notifyServerError(e));
  }, [notifyServerError]);

  useEffect(() => {
    const auths = importedAuthorizations.concat(createdContactAuthorizations);
    let ids = [];

    if (isNewImportToDo) {
      ids = importedAuthorizationsIds.concat(createdContactAuthorizationsIds);
    } else {
      ids = createdContactAuthorizationsIds;
    }

    if (!isEmpty(contactContext.permissionGroupSelect)) {
      setAuthorizations(contactContext.permissionGroupSelect);
      setAuthorizations(prev => [...prev, ...auths]);
    } else {
      setAuthorizations(auths);
    }
    setPermissionsStaging(auths);
    setCurrentAuthorizationsIds(ids);
  }, [
    contactContext.permissionGroupSelect,
    createdContactAuthorizations,
    createdContactAuthorizationsIds,
    importedAuthorizations,
    importedAuthorizationsIds,
    isNewImportToDo,
    permissionGroupsUploaded,
    setPermissionsStaging
  ]);

  const onGroupsFilterChange = useCallback(
    event => setGroupsFilter(event.target.value),
    []
  );

  const [formCtrlLabel1Cls, formCtrlLabel2Cls] = useMemo(
    () => [
      { root: classes.topToolbar },
      { label: classes.radioLabel },
      { root: classes.radioLabelRoot, label: classes.radioLabel }
    ],
    [classes]
  );

  const hasWriteAuthorizationPermission = useMemo(() => {
    return permissions.includes('AUTH_WRITE_USER_AUTHORIZATION');
  }, [permissions]);

  if (
    selectedAssignmentMode === 'import' &&
    importedAuthorizations.length === 0
  ) {
    return (
      <span className={classes.label}>
        There are no Access Rights to be displayed
      </span>
    );
  }

  return (
    <>
      <Grid container justifyContent="space-between" alignItems="center">
        {!!selectedContactForImport && isNewImportToDo && (
          <span className={classes.contactInformation}>
            Contact:{' '}
            <span className={classes.bold} style={{ marginRight: '10px' }}>
              {selectedContactForImport.firstName}{' '}
              {selectedContactForImport.lastName}
            </span>{' '}
            User ID:{' '}
            <span className={classes.bold}>
              {selectedContactForImport.userId}
            </span>
          </span>
        )}

        <RadioGroup
          aria-label="groupsFilter"
          name="groupsFilter"
          value={groupsFilter}
          onChange={onGroupsFilterChange}
          row
          style={{ marginLeft: 'auto', marginTop: 0 }}
        >
          <FormControlLabel
            value="ALL"
            control={<Radio color="primary" id="show-all" />}
            label="Show All"
            classes={formCtrlLabel1Cls}
            id="show-all"
          />
          <FormControlLabel
            value="ONLY_ASSIGNED"
            control={<Radio color="primary" id="show-assigned" />}
            label="Show Assigned"
            classes={formCtrlLabel2Cls}
            style={{ marginRight: 0 }}
          />
        </RadioGroup>
      </Grid>
      <UserAuthorizationDatagrid
        {...props}
        authorizations={authorizations}
        groups={groups}
        hasWriteAuthorizationPermission={hasWriteAuthorizationPermission}
        authorizationsConnectedUser={createUserAuthorizations(
          connectedUserAuthorizations
        )}
        userType={createdUserType}
        createdContactId={createdContactId}
        creation={creation}
        permissionsStaging={permissionsStaging}
        setPermissionsStaging={setPermissionsStaging}
        ids={currentAuthorizationsIds}
        authorizationsGridToDeleteRef={authorizationsGridToDeleteRef}
        groupBy={isCreatedContactRcOrIa ? 'investorId' : 'fundFamilyId'}
        groupByLabel={
          isCreatedContactRcOrIa ? 'investorName' : 'fundFamilyName'
        }
        optionText={'fundName'}
        optionValue={'fundId'}
        wizardMode={true}
        permissionGroupsUploaded={permissionGroupsUploaded}
        groupsFilter={groupsFilter}
      />
    </>
  );
};

export default AssignPermissionGroupsStep;
